import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FieldType } from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { BlockableDiv } from '../../../../shared/controls/blockable-div/blockable-div.component';
import { BaseDetailComponent } from '../../../../common/base-detail.component';
import { Address } from '../../../../generated/generated-types';
import { FormlyFillHeightListHostDirective } from '../../../custom-formly/formly-tabs/formly-fill-height-list-host.directive';
import { WidgetGenericDocumentListComponent } from '../widget-generic-document-list/widget-generic-document-list.component';

@Component({
  selector: 'app-formly-generic-document-list',
  templateUrl: './formly-generic-document-list.component.html',
  hostDirectives: [FormlyFillHeightListHostDirective],
  host: {
    // Content-scroll tabs (e.g. document project): mark host so page scroll CSS engages.
    '[class.phx-base-list--content-height]': 'isContentHeightList',
  },
  standalone: true,
  imports: [
    WidgetGenericDocumentListComponent,
    BlockableDiv,
    ButtonModule,
    TooltipModule,
    TranslatePipe,
  ]
})
export class FormlyGenericDocumentListComponent extends FieldType implements OnInit {

  selectedDocumentType = null;
  isSaving = false;

  /** True when the tab opts out of fill-height (detail `.content` scrolls instead). */
  get isContentHeightList(): boolean {
    return this.props?.fillHeight === false;
  }

  /** When `props.fillHeight === false`, list sizes to rows; detail `.content` scrolls. */
  get fillHeightList(): boolean {
    return !this.isContentHeightList;
  }

  /** Label when the host entity must be saved before the nested list can load. */
  get saveFirstLabelKey(): string {
    const modelType = this.props?.externalModelType;
    if (modelType === 'DOCUMENT_PROJECT') {
      return 'document.project.save-project-first';
    }
    if (modelType === 'CONTRACT') {
      return 'document.save-document-first';
    }
    return 'address.save-address-first';
  }

  get missingDataLabelKey(): string {
    const modelType = this.props?.externalModelType;
    if (modelType === 'DOCUMENT_PROJECT') {
      return 'document.project.missing-data-for-save';
    }
    if (modelType === 'CONTRACT') {
      return 'document.missing-data-for-save';
    }
    return 'address.missing-data-for-save';
  }

  @ViewChild(WidgetGenericDocumentListComponent)
  listWidget?: WidgetGenericDocumentListComponent;

  private destroyRef = inject(DestroyRef);

  constructor(private cdRef: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    // In the contract editor, refetch the list when new contract documents are generated.
    if (this.props?.externalModelType !== 'CONTRACT') {
      return;
    }
    this.options?.formState?.detailComponent?.needsGeneratedDocumentsRefresh$
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.listWidget?.refresh(true));
  }

  checkIfCanSave(): boolean {
    const component = this.options.formState.detailComponent as BaseDetailComponent<Address>;
    return component.detailForm.invalid;
  }

  async saveToUnlockEditMode(): Promise<void> {
    const component = this.options.formState.detailComponent as BaseDetailComponent<Address>;
    if (!component) {
      console.error("No detail component found to save AddressPostalLink");
      return;
    }

    this.isSaving = true;
    this.cdRef.detectChanges();

    await component.save();

    this.isSaving = false;
    this.cdRef.detectChanges();
  }
}
